import React from 'react';
import { StatusHistoryEntry } from '../../types';
import { HistoryIcon } from '../../components/icons/Icons';
import { formatDateShort } from '../../utils/format';

interface ChangeLogCardProps {
  history: StatusHistoryEntry[];
}

/** Etiquetas legibles para el equipo en obra, no nombres de columna. */
const FIELD_LABELS: Record<string, string> = {
  name: 'Task name',
  description: 'Description',
  priority: 'Priority',
  estimated_hours: 'Estimated hours',
  start_date: 'Start date',
  end_date: 'Due date',
  assignee_added: 'Assigned',
  assignee_removed: 'Unassigned',
  extra_work: 'Extra work',
};

const isDateField = (field: string) => field === 'start_date' || field === 'end_date';

const describe = (entry: StatusHistoryEntry): React.ReactNode => {
  const label = FIELD_LABELS[entry.field] || entry.field;

  if (entry.field === 'assignee_added') {
    return <>Assigned <strong>{entry.newValue}</strong></>;
  }
  if (entry.field === 'assignee_removed') {
    return <>Unassigned <strong>{entry.oldValue}</strong></>;
  }
  if (entry.field === 'extra_work') {
    return <>Marked as <strong>extra work</strong> requested by the client</>;
  }

  const from = isDateField(entry.field) && entry.oldValue ? formatDateShort(entry.oldValue) : entry.oldValue;
  const to = isDateField(entry.field) && entry.newValue ? formatDateShort(entry.newValue) : entry.newValue;

  return (
    <>
      Changed <strong>{label}</strong>
      {from ? <> from <span className="changelog-value">{from}</span></> : null}
      {to ? <> to <span className="changelog-value">{to}</span></> : <> (cleared)</>}
    </>
  );
};

/**
 * Historial de cambios de requerimientos. Vive aparte del Activity a
 * propósito: si estas entradas se mezclaran con los comentarios, los
 * sepultarían en cuanto alguien reajuste fechas un par de veces.
 */
export const ChangeLogCard: React.FC<ChangeLogCardProps> = ({ history }) => {
  const changes = history
    .filter(h => h.field !== 'status')
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return (
    <div className="detail-card full-width">
      <h3 className="detail-card-title">
        <HistoryIcon size={16} />
        Change History ({changes.length})
      </h3>

      {changes.length === 0 ? (
        <p className="no-data-message">
          No requirement changes recorded yet. Edits to dates, scope or personnel will appear here.
        </p>
      ) : (
        <div className="changelog-list">
          {changes.map(entry => (
            <div key={entry.id} className={`changelog-item${entry.field === 'extra_work' ? ' changelog-item-extra' : ''}`}>
              <div className="changelog-header">
                <strong>{entry.changedByName}</strong>
                <span>{new Date(entry.timestamp).toLocaleString()}</span>
              </div>
              <p className="changelog-description">{describe(entry)}</p>
              {entry.comment && <p className="changelog-comment">{entry.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
